// Cloud Rules page: author named QROS rules.
//
// A rule says what follows from the data: when its conditions hold, it adds a
// fact (an inference), or it only picks out the things that match (a
// selector), which a query or a fact's context can refer to as `rule:<name>`.
// Rules are built in a form for the common shapes (components/RuleBuilder,
// lib/ruleBuilder) or written as QQL; either way the text is saved through one
// endpoint that compiles it and refuses what does not compile.
//
// Design rules:
//   - QROS-only. The page renders a notice on the RDF backend and the nav entry
//     is hidden there; the API enforces the same gate.
//   - Reads/writes go through the substrate (RuleStore + inference engine).
//   - Writes need ImportData (Admin + Power User); reads are open to any
//     authenticated user (read-only for the User role).
//   - Row actions live in the LEFT-most column (the house table convention).

import { useMemo, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "react-oidc-context";
import { canImportData, isQros, useMe } from "@/lib/useMe";
import { SubTabs } from "@/components/SubTabs";
import { cn } from "@/lib/utils";
import {
  loadRulesFile,
  preflightRules,
  type RuleLoadReport,
  type RulePreflight,
} from "@/lib/api";
import {
  createRule,
  deleteRule,
  isSelector,
  isSystemRule,
  ruleScopeToken,
  testRule,
  updateRule,
  useRuleDerivations,
  useRuleNameAudit,
  useRules,
  type RuleDef,
  type RuleExtension,
  type RuleNameAudit,
} from "@/lib/useRules";
import { pageModule, useModulesConfig } from "@/lib/usePages";
import { BetaBadge } from "@/components/ModuleBadge";
import { messageClass } from '@/components/ui/Message';
import { Button, buttonVariants } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { fieldClass } from '@/components/ui/field';
import { ALL_GRAPHS } from '@/components/GraphSelector';
import { RuleBuilder, RuleReadings } from '@/components/RuleBuilder';
import { localName, useOntologySchema } from '@/lib/useOntology';
import { useEntityLabels } from '@/lib/useEntity';
import {
  blankBuilderRule,
  builderProblems,
  describeRule,
  generateRule,
  parseRule,
  type BuilderRule,
} from '@/lib/ruleBuilder';

// A rule's WHEN/THEN are raw QQL text (the native rule model), the same syntax
// authored in a `.tqs` `@rules` block. The QQL editor carries them verbatim.
interface RuleForm {
  name: string;
  description: string;
  when: string;
  then: string;
  enabled: boolean;
}

function blankForm(): RuleForm {
  return { name: "", description: "", when: "", then: "", enabled: true };
}

function formFromRule(rule: RuleDef): RuleForm {
  return {
    name: rule.name,
    description: rule.description,
    when: rule.when,
    then: rule.then,
    enabled: rule.enabled,
  };
}

function formToRule(form: RuleForm): RuleDef {
  return {
    name: form.name.trim(),
    description: form.description.trim(),
    when: form.when.trim(),
    then: form.then.trim(),
    enabled: form.enabled,
  };
}

export function RulesPage() {
  const auth = useAuth();
  const me = useMe();
  const token = auth.user?.id_token;
  const qros = isQros(me.data);
  const canEdit = canImportData(me.data);
  const modulesConfig = useModulesConfig();
  const queryClient = useQueryClient();

  const rulesQuery = useRules(qros);
  const audit = useRuleNameAudit(qros);
  const derivations = useRuleDerivations(qros);
  const derivedByRule = useMemo(
    () =>
      new Map((derivations.data?.rules ?? []).map((r) => [r.name, r.derived])),
    [derivations.data],
  );
  const rules = useMemo(
    () =>
      [...(rulesQuery.data ?? [])].sort((a, b) => a.name.localeCompare(b.name)),
    [rulesQuery.data],
  );
  // The set view (22.22): every named ruleset as a section, its members in
  // name order, then the rules that belong to no set. A set is the unit a file
  // load replaces, so it is the unit the page reads by.
  const sections = useMemo(() => {
    const bySet = new Map<string, RuleDef[]>();
    const unnamed: RuleDef[] = [];
    for (const rule of rules) {
      if (rule.ruleset) {
        const list = bySet.get(rule.ruleset) ?? [];
        list.push(rule);
        bySet.set(rule.ruleset, list);
      } else {
        unnamed.push(rule);
      }
    }
    // The system set leads: what TReS ships runs before anything authored here.
    const named = [...bySet.entries()]
      .sort(([a], [b]) => {
        if (isSystemRule({ ruleset: a })) return -1;
        if (isSystemRule({ ruleset: b })) return 1;
        return a.localeCompare(b);
      })
      .map(([set, members]) => ({ set, members }));
    return unnamed.length > 0
      ? [...named, { set: null, members: unnamed }]
      : named;
  }, [rules]);

  // editor: null = closed; { rule: null } = create; { rule } = edit existing.
  const [editor, setEditor] = useState<{ original: RuleDef | null } | null>(
    null,
  );
  const [deleting, setDeleting] = useState<RuleDef | null>(null);
  const [switching, setSwitching] = useState<RuleDef | null>(null);

  // Rules-file load: preflight first, then a confirmation stated in rule
  // terms — never a graph, never a mode. That is the point of this path.
  const fileInput = useRef<HTMLInputElement | null>(null);
  const [pendingLoad, setPendingLoad] = useState<{
    fileName: string;
    content: string;
    preflight: RulePreflight;
  } | null>(null);
  const [loadReport, setLoadReport] = useState<RuleLoadReport | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loadBusy, setLoadBusy] = useState(false);

  async function handleFileChosen(file: File) {
    if (!token) return;
    setLoadError(null);
    const content = await file.text();
    const pf = await preflightRules(content, token);
    if (!pf.ok) {
      setLoadError(pf.message);
      return;
    }
    setPendingLoad({ fileName: file.name, content, preflight: pf.data });
  }

  async function handleLoadConfirmed() {
    if (!token || !pendingLoad) return;
    setLoadBusy(true);
    const r = await loadRulesFile(pendingLoad.content, token);
    setLoadBusy(false);
    setPendingLoad(null);
    if (r.ok) {
      setLoadReport(r.data);
      void queryClient.invalidateQueries({ queryKey: ["rules"] });
      void queryClient.invalidateQueries({ queryKey: ["rules-derivations"] });
      void queryClient.invalidateQueries({ queryKey: ["rules-name-audit"] });
    } else {
      setLoadError(r.message);
    }
  }

  if (!qros) {
    return (
      <div className="container mx-auto py-6">
        <h1 className="text-2xl font-semibold text-tres-text-primary">Rules</h1>
        <div className={cn(messageClass('platform', 'normal', 'warning'), 'mt-4')}>
          Rules are a QROS-native feature. This instance uses a different
          backend, so rule authoring is unavailable.
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-6">
      <SubTabs
        tabs={[
          { to: "/ontology", label: "Ontology" },
          { to: "/rules", label: "Rules" },
        ]}
      />
      <div className="mb-4 flex items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-tres-text-primary">
            Rules
            <BetaBadge pm={pageModule(modulesConfig.data, "rules")} />
          </h1>
          <p className="text-sm text-tres-text-secondary">
            A rule says what follows from your data: when its conditions hold,
            it adds a fact, or it only picks out the things that match.
            {!canEdit && " Read-only: your role cannot author rules."}
          </p>
        </div>
        {canEdit && (
          <div className="flex gap-2">
            <input
              ref={fileInput}
              type="file"
              accept=".tqs"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                e.target.value = "";
                if (f) void handleFileChosen(f);
              }}
            />
            <button
              type="button"
              onClick={() => fileInput.current?.click()}
              className={buttonVariants({ variant: 'outline', size: 'md' })}
            >
              Load rules file…
            </button>
            <button
              type="button"
              onClick={() => setEditor({ original: null })}
              className={buttonVariants({ variant: 'primary', size: 'md' })}
            >
              New rule
            </button>
          </div>
        )}
      </div>

      {loadError && (
        <p className={cn(messageClass('warning', 'normal'), 'mb-3')}>
          {loadError}
        </p>
      )}

      {derivations.data?.failure && (
        <div className={cn(messageClass('warning', 'normal'), 'mb-3')} role="alert">
          Derived facts were not recomputed after the last change: {derivations.data.failure}.
          The change itself was saved, and the derived facts are those of the last run that
          succeeded. Change or turn off the rule it names; the next change recomputes them.
        </div>
      )}

      {rulesQuery.isLoading && (
        <p className="text-sm text-tres-text-secondary">Loading rules…</p>
      )}
      {rulesQuery.isError && (
        <p className="text-sm text-tres-error">
          Failed to load rules: {(rulesQuery.error as Error).message}
        </p>
      )}
      {!rulesQuery.isLoading && !rulesQuery.isError && rules.length === 0 && (
        <p className="text-sm text-tres-text-secondary">
          No rules yet.{canEdit && " Click “New rule” to author one."}
        </p>
      )}

      {rules.length > 0 && (
        <div className="overflow-x-auto rounded-md border border-border">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="bg-tres-submenu-bg text-left text-tres-text-secondary">
                {canEdit && (
                  <th className="w-px whitespace-nowrap px-3 py-2 font-medium">
                    Actions
                  </th>
                )}
                <th className="px-3 py-2 font-medium">Name</th>
                <th className="px-3 py-2 font-medium">Type</th>
                <th className="px-3 py-2 text-right font-medium">Derived</th>
                <th
                  className="px-3 py-2 font-medium"
                  title="How a query or a fact's context refers to the things this rule picks out"
                >
                  Refer to it as
                </th>
                <th className="px-3 py-2 font-medium">Definition</th>
              </tr>
            </thead>
            <tbody>
              {sections.map((section) => [
                <tr
                  key={section.set ?? "(unnamed)"}
                  className="border-t border-border bg-tres-submenu-bg/60"
                >
                  <td colSpan={canEdit ? 6 : 5} className="px-3 py-1.5 text-xs">
                    {section.set && isSystemRule({ ruleset: section.set }) ? (
                      <>
                        <span className="font-semibold text-tres-text-primary">
                          System rules
                        </span>
                        <span className="text-tres-text-secondary">
                          {" "}
                          — shipped with TReS and always listed. Each can be
                          turned off here and stays off until you turn it back
                          on; the text is maintained by TReS and cannot be
                          edited or deleted. They run before any rule authored
                          here.
                        </span>
                      </>
                    ) : section.set ? (
                      <>
                        <span className="font-semibold text-tres-text-primary">
                          Ruleset {section.set}
                        </span>
                        <span className="text-tres-text-secondary">
                          {" "}
                          — {section.members.length} rule
                          {section.members.length === 1 ? "" : "s"}; loading a
                          file with this set replaces all of them
                        </span>
                      </>
                    ) : (
                      <>
                        <span className="font-semibold text-tres-text-primary">
                          Not in a named set
                        </span>
                        <span className="text-tres-text-secondary">
                          {" "}
                          — {section.members.length} rule
                          {section.members.length === 1 ? "" : "s"} authored
                          here or loaded from an unnamed block; a set load never
                          removes them
                        </span>
                      </>
                    )}
                  </td>
                </tr>,
                ...section.members.map((rule) => (
                  <tr
                    key={rule.name}
                    className="border-t border-border align-top"
                  >
                    {canEdit && (
                      <td className="whitespace-nowrap px-3 py-2">
                        <div className="flex gap-1">
                          <button
                            type="button"
                            onClick={() => setSwitching(rule)}
                            className={buttonVariants({ variant: 'outline', size: 'sm' })}
                          >
                            {rule.enabled ? "Turn off" : "Turn on"}
                          </button>
                          {!isSystemRule(rule) && (
                            <>
                              <button
                                type="button"
                                onClick={() => setEditor({ original: rule })}
                                className={buttonVariants({ variant: 'outline', size: 'sm' })}
                              >
                                Edit
                              </button>
                              <button
                                type="button"
                                onClick={() => setDeleting(rule)}
                                className={buttonVariants({ variant: 'danger', size: 'sm' })}
                              >
                                Delete
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    )}
                    <td className="px-3 py-2">
                      <div
                        className={cn(
                          "font-medium",
                          rule.enabled
                            ? "text-tres-text-primary"
                            : "text-tres-text-secondary",
                        )}
                      >
                        {rule.name}
                        {!rule.enabled && (
                          <span className="ml-2 rounded bg-tres-submenu-bg px-1.5 py-0.5 text-xs font-normal text-tres-text-secondary">
                            off
                          </span>
                        )}
                      </div>
                      {rule.description && (
                        <div className="text-xs text-tres-text-secondary">
                          {rule.description}
                        </div>
                      )}
                      {rule.problem && (
                        <div className={cn(messageClass('warning', 'normal'), 'mt-1 text-xs')} role="alert">
                          Does not run: {rule.problem}.{' '}
                          {canEdit
                            ? isSystemRule(rule)
                              ? 'Turn it off until the next build.'
                              : 'Edit it, or turn it off.'
                            : 'Ask an editor to fix it or turn it off.'}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2 text-tres-text-secondary">
                      {isSelector(rule) ? "Selector" : "Inference"}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-tres-text-secondary">
                      {isSelector(rule)
                        ? "—"
                        : (derivedByRule.get(rule.name) ?? 0).toLocaleString()}
                    </td>
                    <td className="px-3 py-2">
                      <RuleReference name={rule.name} />
                    </td>
                    <td className="px-3 py-2">
                      <RuleSummary rule={rule} />
                    </td>
                  </tr>
                )),
              ])}
            </tbody>
          </table>
        </div>
      )}

      <NameAuditPanel
        audit={audit.data}
        canEdit={canEdit}
        onDelete={(name) => {
          const rule = rules.find((r) => r.name === name);
          if (rule) setDeleting(rule);
        }}
      />

      {editor && (
        <RuleEditorModal
          original={editor.original}
          existingNames={rules.map((r) => r.name)}
          token={token}
          onClose={() => setEditor(null)}
          onSaved={() => {
            setEditor(null);
            void rulesQuery.refetch();
          }}
        />
      )}

      {switching && (
        <SwitchConfirm
          rule={switching}
          dependents={
            switching.enabled && switching.name === "BroaderPropagation"
              ? (derivations.data?.ladder_dependents ?? [])
              : []
          }
          token={token}
          onClose={() => setSwitching(null)}
          onSwitched={() => {
            setSwitching(null);
            void rulesQuery.refetch();
          }}
        />
      )}

      {deleting && (
        <DeleteConfirm
          rule={deleting}
          token={token}
          onClose={() => setDeleting(null)}
          onDeleted={() => {
            setDeleting(null);
            void rulesQuery.refetch();
            void audit.refetch();
          }}
        />
      )}

      {pendingLoad && (
        <LoadRulesConfirm
          fileName={pendingLoad.fileName}
          preflight={pendingLoad.preflight}
          busy={loadBusy}
          onClose={() => setPendingLoad(null)}
          onConfirm={() => void handleLoadConfirmed()}
        />
      )}

      {loadReport && (
        <LoadRulesResult
          report={loadReport}
          onClose={() => setLoadReport(null)}
        />
      )}
    </div>
  );
}

/** Confirmation for a rules-file load, stated entirely in rule terms: which
 *  named sets replace their membership, what that retracts, and how many
 *  unnamed rules upsert (their old names are NOT retracted). No graph, no
 *  mode: this path has neither. */
function LoadRulesConfirm({
  fileName,
  preflight,
  busy,
  onClose,
  onConfirm,
}: {
  fileName: string;
  preflight: RulePreflight;
  busy: boolean;
  onClose: () => void;
  onConfirm: () => void;
}) {
  const deletes = preflight.total_would_retract;
  return (
    <Modal
      title="Load rules file"
      onClose={onClose}
      busy={busy}
      danger={deletes > 0}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button variant={deletes > 0 ? 'dangerFilled' : 'primary'} onClick={onConfirm} disabled={busy}>
            {busy
              ? "Loading…"
              : deletes > 0
                ? `Load and delete ${deletes} rule${deletes === 1 ? "" : "s"}`
                : "Load rules"}
          </Button>
        </>
      }
    >
      <p className="text-sm text-tres-text-primary">
        <span className="font-mono">{fileName}</span>
      </p>
      <div className="mt-3 space-y-3">
        {preflight.sets.map((s) => (
          <div key={s.set} className="rounded border border-border p-3">
            <p className="text-sm text-tres-text-primary">
              Ruleset <span className="font-medium">{s.set}</span>:{" "}
              {s.incoming} rule
              {s.incoming === 1 ? "" : "s"} in the file
              {s.would_retract.length === 0
                ? "; nothing is removed."
                : `; replaces the set and deletes ${s.would_retract.length} stored rule${
                    s.would_retract.length === 1 ? "" : "s"
                  }:`}
            </p>
            {s.would_retract.length > 0 && (
              <p className="mt-1 whitespace-pre-wrap font-mono text-xs text-tres-error">
                {s.would_retract.join("\n")}
              </p>
            )}
          </div>
        ))}
        {preflight.unnamed > 0 && (
          <p className="text-sm text-tres-text-secondary">
            {preflight.unnamed === 1
              ? "One rule in this file is not part of a named rule set. It will be added, or, if a stored rule has exactly the same name, it will update that rule."
              : `${preflight.unnamed} rules in this file are not part of a named rule set. Each will be added, or, if a stored rule has exactly the same name, it will update that rule.`}{" "}
            Nothing is deleted: if a rule was renamed, the old name stays in the
            store as its own rule until you delete it. The Name audit below the
            rule list shows any such leftovers.
          </p>
        )}
      </div>
    </Modal>
  );
}

/** What the load actually did, in the same rule terms the confirmation used. */
function LoadRulesResult({
  report,
  onClose,
}: {
  report: RuleLoadReport;
  onClose: () => void;
}) {
  return (
    <Modal
      title="Rules loaded"
      onClose={onClose}
      footer={
        <Button variant="primary" onClick={onClose}>
          Close
        </Button>
      }
    >
      <div className="space-y-2 text-sm text-tres-text-primary">
        <p>
          {report.loaded.length} rule{report.loaded.length === 1 ? "" : "s"}{" "}
          loaded
          {report.retracted.length > 0 &&
            `; ${report.retracted.length} retracted by set replacement`}
          ; {report.derived} statement{report.derived === 1 ? "" : "s"} derived.
        </p>
        {report.retracted.length > 0 && (
          <p className="whitespace-pre-wrap font-mono text-xs text-tres-text-secondary">
            {report.retracted.map((r) => `${r.set}: ${r.name}`).join("\n")}
          </p>
        )}
        {report.failed.length > 0 && (
          <div>
            <p className="text-tres-error">
              {report.failed.length} rule{report.failed.length === 1 ? "" : "s"}{" "}
              did not reach the store:
            </p>
            <p className="whitespace-pre-wrap font-mono text-xs text-tres-error">
              {report.failed.map((f) => `${f.name}: ${f.error}`).join("\n")}
            </p>
          </div>
        )}
        {report.materialization_error && (
          <p className={messageClass('platform', 'compact', 'warning')}>
            Derivation did not run to completion: {report.materialization_error}
          </p>
        )}
      </div>
    </Modal>
  );
}

/** Stored names that predate the naming policy: legal history that keeps
 *  working, surfaced so cleanup is a deliberate act. Renders nothing when the
 *  store is clean. */
function NameAuditPanel({
  audit,
  canEdit,
  onDelete,
}: {
  audit: RuleNameAudit | undefined;
  canEdit: boolean;
  onDelete: (name: string) => void;
}) {
  if (!audit) return null;
  const dirty =
    audit.invalid_names.length > 0 || audit.case_collisions.length > 0;
  if (!dirty) return null;
  return (
    <div className={cn(messageClass('platform', 'normal', 'warning'), 'mt-6')}>
      <h2 className="text-sm font-semibold text-tres-text-primary">
        Name audit
      </h2>
      <p className="mt-1 text-xs text-tres-text-secondary">
        These stored rules predate the naming standard. They keep working, but a
        query or a fact's context cannot refer to them by name, and they block a
        clean export. Deleting one is a rule edit: review before acting.
      </p>
      <ul className="mt-3 space-y-1">
        {audit.invalid_names.map((n) => (
          <li key={n.name} className="flex items-center gap-2 text-sm">
            {canEdit && (
              <Button variant="danger" size="sm" onClick={() => onDelete(n.name)}>
                Delete
              </Button>
            )}
            <span className="font-mono text-tres-text-primary">{n.name}</span>
            <span className="text-xs text-tres-text-secondary">{n.reason}</span>
          </li>
        ))}
        {audit.case_collisions.map((c) => (
          <li
            key={`${c.name}~${c.collides_with}`}
            className="flex items-center gap-2 text-sm"
          >
            {canEdit && (
              <Button variant="danger" size="sm" onClick={() => onDelete(c.name)}>
                Delete
              </Button>
            )}
            <span className="font-mono text-tres-text-primary">{c.name}</span>
            <span className="text-xs text-tres-text-secondary">
              fires alongside {c.collides_with}: same name in a different case
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** How a query or a fact's context refers to the things a rule picks out;
 *  pressing it copies the reference. */
function RuleReference({ name }: { name: string }) {
  const [copied, setCopied] = useState<"yes" | "no" | null>(null);
  const reference = ruleScopeToken(name);
  return (
    <button
      type="button"
      title="Copy this, to refer in a query or in a fact's context to the things this rule picks out"
      onClick={() => {
        const done = (result: "yes" | "no") => {
          setCopied(result);
          setTimeout(() => setCopied(null), 1500);
        };
        if (!navigator.clipboard) {
          done("no");
          return;
        }
        navigator.clipboard.writeText(reference).then(
          () => done("yes"),
          () => done("no"),
        );
      }}
      className={cn(buttonVariants({ variant: 'ghost', size: 'sm' }), 'bg-muted font-mono text-tres-text-primary hover:bg-muted/80')}
    >
      {copied === "yes" ? "Copied" : copied === "no" ? "Copy refused by the browser" : reference}
    </button>
  );
}

function RuleSummary({ rule }: { rule: RuleDef }) {
  return (
    <div className="whitespace-pre-wrap font-mono text-xs text-tres-text-secondary">
      <span className="text-tres-text-primary">WHEN</span> {rule.when}
      {rule.then.trim().length > 0 && (
        <>
          {"\n"}
          <span className="text-tres-text-primary">THEN</span> {rule.then}
        </>
      )}
    </div>
  );
}

interface EditorProps {
  original: RuleDef | null;
  existingNames: string[];
  token: string | undefined;
  onClose: () => void;
  onSaved: () => void;
}

type EditorMode = "build" | "qql";

/** Every term the form names, for the labels its reading needs. */
function termsOf(rule: BuilderRule): string[] {
  const out = new Set<string>();
  const add = (t: string) => t && out.add(t);
  add(rule.subjectClass);
  if (rule.link) {
    add(rule.link.property);
    if (rule.link.cls) add(rule.link.cls);
  }
  for (const c of [...rule.conditions, ...rule.unless]) {
    add(c.property);
    if (c.test.kind === "is") add(c.test.term);
    if (c.test.kind === "anyOf" && c.test.valueKind === "thing") c.test.values.forEach(add);
    if (c.test.kind === "compareProperty") add(c.test.property);
  }
  const o = rule.outcome;
  if (o.kind === "type") add(o.cls);
  if (o.kind === "link") {
    add(o.property);
    add(o.term);
  }
  if (o.kind === "value") add(o.property);
  return [...out];
}

export function RuleEditorModal({
  original,
  existingNames,
  token,
  onClose,
  onSaved,
}: EditorProps) {
  const queryClient = useQueryClient();
  const isNew = original === null;
  const opened = useMemo(
    () => (original ? parseRule(original.when, original.then) : null),
    [original],
  );
  const [form, setForm] = useState<RuleForm>(() =>
    original ? formFromRule(original) : blankForm(),
  );
  const [mode, setMode] = useState<EditorMode>(
    isNew || opened ? "build" : "qql",
  );
  const [builder, setBuilder] = useState<BuilderRule>(
    () => opened ?? blankBuilderRule(),
  );
  const [modeNote, setModeNote] = useState<string | null>(
    !isNew && !opened
      ? "This rule's text is not one of the shapes the builder writes, so it opens as QQL."
      : null,
  );
  const [cannotBuild, setCannotBuild] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [extension, setExtension] = useState<RuleExtension | null>(null);
  const [testing, setTesting] = useState(false);

  const schema = useOntologySchema(ALL_GRAPHS, token);
  const schemaTerms = useMemo(() => {
    const m = new Map<string, string>();
    for (const c of schema.data?.classes ?? []) if (c.label) m.set(c.uri, c.label);
    for (const p of schema.data?.properties ?? []) if (p.label) m.set(p.uri, p.label);
    return m;
  }, [schema.data]);
  const unlabeled = termsOf(builder).filter((t) => !schemaTerms.has(t));
  const entityLabels = useEntityLabels(unlabeled, ALL_GRAPHS);
  const name = (term: string) =>
    schemaTerms.get(term) ?? entityLabels.get(term) ?? localName(term);
  const problems = builderProblems(builder);
  const written = problems.length === 0 ? generateRule(builder) : null;

  const nameClash =
    isNew &&
    existingNames.includes(form.name.trim()) &&
    form.name.trim() !== "";

  function patch(p: Partial<RuleForm>) {
    setForm((f) => ({ ...f, ...p }));
    setExtension(null);
  }

  function patchBuilder(next: BuilderRule) {
    setBuilder(next);
    setExtension(null);
    setError(null);
  }

  function switchTo(next: EditorMode) {
    setError(null);
    setExtension(null);
    if (next === mode) return;
    if (next === "qql") {
      if (written) {
        setForm((f) => ({ ...f, when: written.when, then: written.then }));
        setModeNote(null);
      } else {
        setModeNote("The form is not finished, so the QQL editor shows the text as it was.");
      }
      setCannotBuild(false);
      setMode("qql");
      return;
    }
    const parsed = parseRule(form.when, form.then);
    if (!form.when.trim() && !form.then.trim()) {
      setMode("build");
      setModeNote(null);
      return;
    }
    if (!parsed) {
      setCannotBuild(true);
      return;
    }
    setBuilder(parsed);
    setCannotBuild(false);
    setModeNote(null);
    setMode("build");
  }

  function validate(): RuleDef | null {
    const base = formToRule(form);
    if (!base.name) {
      setError("Give the rule a name.");
      return null;
    }
    if (nameClash) {
      setError(`A rule named “${base.name}” already exists.`);
      return null;
    }
    if (mode === "build") {
      if (!written) {
        setError(`The rule is not finished: ${problems.join(" ")}`);
        return null;
      }
      setError(null);
      return { ...base, when: written.when, then: written.then };
    }
    if (!base.when) {
      setError("A rule needs a WHEN clause.");
      return null;
    }
    setError(null);
    return base;
  }

  async function handleTest() {
    if (!token) return;
    const rule = validate();
    if (!rule) return;
    setTesting(true);
    setExtension(null);
    const r = await testRule(rule, token);
    setTesting(false);
    if (r.ok) {
      setExtension(r.data);
    } else {
      setError(r.message);
    }
  }

  async function handleSave() {
    if (!token) return;
    const rule = validate();
    if (!rule) return;
    setBusy(true);
    const r = isNew
      ? await createRule(rule, token)
      : await updateRule(rule, token);
    setBusy(false);
    if (r.ok) {
      void queryClient.invalidateQueries({ queryKey: ["rules"] });
      void queryClient.invalidateQueries({ queryKey: ["rules-derivations"] });
      onSaved();
    } else {
      setError(r.message);
    }
  }

  return (
    <Modal
      title={isNew ? "New rule" : `Edit rule: ${original?.name ?? ""}`}
      onClose={onClose}
      busy={busy}
      size="xl"
      footer={
        // A refusal answers Test or Save, so it sits with them, in view
        // however far the rule body is scrolled.
        <div className="flex w-full flex-col gap-2">
          {error && (
            <div className={messageClass('warning', 'normal')} role="alert">
              {error}
            </div>
          )}
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              className="mr-auto"
              onClick={() => void handleTest()}
              disabled={testing || !token}
            >
              {testing ? "Testing…" : "Test rule"}
            </Button>
            <Button variant="secondary" onClick={onClose} disabled={busy}>
              Cancel
            </Button>
            <Button variant="primary" onClick={() => void handleSave()} disabled={busy || !token}>
              {busy ? "Saving…" : isNew ? "Create" : "Save"}
            </Button>
          </div>
        </div>
      }
    >
      <div className="space-y-4">
        <div className="grid gap-3 md:grid-cols-2">
          <div>
            <label htmlFor="rule-name" className="block text-sm font-medium text-tres-text-primary">
              Name
            </label>
            <input
              id="rule-name"
              className={fieldClass(nameClash ? 'error' : 'normal', 'w-full')}
              value={form.name}
              disabled={!isNew}
              onChange={(e) => patch({ name: e.target.value })}
              placeholder="LargeModels"
            />
            <p className="mt-1 text-xs text-tres-text-secondary">
              {isNew
                ? "Letters, digits, _ and - only. The name is how the rule is known and cannot be changed later."
                : "The name is the rule's identity and can't be changed. Delete and recreate to rename."}
            </p>
            {nameClash && (
              <p className="mt-1 text-xs text-tres-error">
                A rule with this name already exists.
              </p>
            )}
          </div>
          <div>
            <label htmlFor="rule-description" className="block text-sm font-medium text-tres-text-primary">
              Description
            </label>
            <input
              id="rule-description"
              className={fieldClass('normal', 'w-full')}
              value={form.description}
              onChange={(e) => patch({ description: e.target.value })}
              placeholder="Optional: what this rule is for"
            />
            <p className="mt-1 text-xs text-tres-text-secondary">
              A query or a fact's context refers to the things it picks out as{" "}
              <code className="font-mono">
                {ruleScopeToken(form.name.trim() || "name")}
              </code>
              .
            </p>
          </div>
        </div>

        <div>
          <div className="flex gap-1" role="tablist" aria-label="How to write the rule">
            {(["build", "qql"] as const).map((m) => (
              <Button
                key={m}
                role="tab"
                aria-selected={mode === m}
                variant={mode === m ? "primary" : "secondary"}
                size="sm"
                onClick={() => switchTo(m)}
              >
                {m === "build" ? "Build" : "Write QQL"}
              </Button>
            ))}
          </div>
          {cannotBuild && (
            <div className={cn(messageClass('warning', 'normal'), 'mt-2')} role="alert">
              The builder cannot show this text: it reads only the shapes it
              writes. Keep editing it here, or start the rule again in the
              builder.{" "}
              <Button
                variant="link"
                onClick={() => {
                  setBuilder(blankBuilderRule());
                  setCannotBuild(false);
                  setModeNote(null);
                  setMode("build");
                }}
              >
                Start again in the builder
              </Button>
            </div>
          )}
          {modeNote && !cannotBuild && (
            <p className={cn(messageClass('guidance', 'compact'), 'mt-2')}>{modeNote}</p>
          )}
        </div>

        {mode === "build" ? (
          schema.data ? (
            <>
              <RuleBuilder
                rule={builder}
                onChange={patchBuilder}
                schema={schema.data}
                name={name}
              />
              <RuleReadings
                sentence={describeRule(builder, name)}
                when={written?.when ?? ""}
                then={written?.then ?? ""}
                problems={problems}
              />
            </>
          ) : schema.isError ? (
            <div className={messageClass('warning', 'normal')}>
              The ontology could not be read, so the builder has nothing to
              offer: {schema.error?.message}. Write the rule as QQL instead.
            </div>
          ) : (
            <p className="text-sm text-tres-text-secondary">Reading the ontology…</p>
          )
        ) : (
          <>
            <ClauseEditor
              label="WHEN"
              help="The conditions that must match, in QQL: the same syntax as a .tqs @rules block. Use ?names for the things matched; FILTER and FILTER NOT EXISTS are supported."
              value={form.when}
              onChange={(v) => patch({ when: v })}
              placeholder={
                "?site :locatedIn ?region .\n?region :locatedIn ?country"
              }
            />
            <ClauseEditor
              label="THEN (optional)"
              help="The facts the rule adds. Leave empty for a rule that only picks things out. Every ?name here must appear in WHEN."
              value={form.then}
              onChange={(v) => patch({ then: v })}
              placeholder="?site :locatedIn ?country ."
            />
          </>
        )}

        {extension && (
          <div className="rounded border border-border bg-tres-submenu-bg/40 px-3 py-2 text-sm">
            <div className="font-medium text-tres-text-primary">
              The rule matches {extension.total}{" "}
              {extension.total === 1 ? "thing" : "things"} now
              {extension.truncated &&
                ` (showing the first ${extension.members.length})`}
            </div>
            {extension.members.length === 0 ? (
              <EmptyExtensionReason extension={extension} />
            ) : (
              <ul className="mt-1 max-h-40 space-y-0.5 overflow-y-auto">
                {extension.members.map((m) => (
                  <li key={m.uri} className="text-xs text-tres-text-secondary">
                    <span className="text-tres-text-primary">
                      {m.pref_label}
                    </span>{" "}
                    <span className="font-mono">{m.uri}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
}

// Why an extension came back empty. A term the WHEN names that the store has
// never seen is an authoring error (usually a case or spelling slip), and the
// backend says which term and what it looks like; only when every referenced
// term exists is "no data matches" the honest answer.
function EmptyExtensionReason({ extension }: { extension: RuleExtension }) {
  // Tolerates an API that predates the diagnostic (SPA and server deploy
  // separately).
  const unknown = extension.unknown_terms ?? [];
  if (unknown.length === 0) {
    return (
      <p className="mt-1 text-xs text-tres-text-secondary">
        Nothing in the data matches this rule now.
      </p>
    );
  }
  return (
    <ul className="mt-1 space-y-0.5">
      {unknown.map((u) => (
        <li key={u.term} className="text-xs text-tres-warning">
          {u.message}
        </li>
      ))}
    </ul>
  );
}

interface ClauseEditorProps {
  label: string;
  help: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}

function ClauseEditor({
  label,
  help,
  value,
  onChange,
  placeholder,
}: ClauseEditorProps) {
  const id = `rule-clause-${label.split(" ")[0]?.toLowerCase() ?? "clause"}`;
  return (
    <div>
      <label htmlFor={id} className="block text-sm font-medium text-tres-text-primary">
        {label}
      </label>
      <p className="mb-1 text-xs text-tres-text-secondary">{help}</p>
      <textarea
        id={id}
        className={fieldClass('normal', 'min-h-[4.5rem] w-full font-mono')}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        spellCheck={false}
        rows={3}
      />
    </div>
  );
}

/** Turning a rule off keeps it stored and listed; it derives nothing and
 *  picks out nothing until turned back on. Switching BroaderPropagation off
 *  names the rules that only match through the vocabulary ladder. */
function SwitchConfirm({
  rule,
  dependents,
  token,
  onClose,
  onSwitched,
}: {
  rule: RuleDef;
  dependents: string[];
  token: string | undefined;
  onClose: () => void;
  onSwitched: () => void;
}) {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const turningOff = rule.enabled;

  async function handleSwitch() {
    if (!token) return;
    setBusy(true);
    const r = await updateRule({ ...rule, enabled: !rule.enabled }, token);
    setBusy(false);
    if (r.ok) {
      void queryClient.invalidateQueries({ queryKey: ["rules"] });
      void queryClient.invalidateQueries({ queryKey: ["rules-derivations"] });
      onSwitched();
    } else {
      setError(r.message);
    }
  }

  return (
    <Modal
      title={turningOff ? "Turn rule off" : "Turn rule on"}
      onClose={onClose}
      busy={busy}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button variant="primary" onClick={() => void handleSwitch()} disabled={busy}>
            {busy ? "Saving…" : turningOff ? "Turn off" : "Turn on"}
          </Button>
        </>
      }
    >
      <p className="text-sm text-tres-text-primary">
        {turningOff ? (
          <>
            Turn off <span className="font-medium">{rule.name}</span>? It stays
            listed here, but derives nothing and picks out nothing until you
            turn it back on. Facts it derived are removed on the next rule run.
          </>
        ) : (
          <>
            Turn on <span className="font-medium">{rule.name}</span>? It derives
            again on the next rule run.
          </>
        )}
      </p>
      {turningOff && dependents.length > 0 && (
        <div className={cn(messageClass('platform', 'normal', 'warning'), 'mt-3')}>
          <p className="text-tres-text-primary">
            {dependents.length === 1 ? "This rule names" : "These rules name"} a
            vocabulary term that data reaches only through broader propagation,
            and will stop matching that data:
          </p>
          <ul className="mt-1 list-disc pl-5 text-tres-text-secondary">
            {dependents.map((name) => (
              <li key={name}>{name}</li>
            ))}
          </ul>
        </div>
      )}
      {error && <p className={cn(messageClass('warning', 'normal'), 'mt-2')}>{error}</p>}
    </Modal>
  );
}

function DeleteConfirm({
  rule,
  token,
  onClose,
  onDeleted,
}: {
  rule: RuleDef;
  token: string | undefined;
  onClose: () => void;
  onDeleted: () => void;
}) {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function handleDelete() {
    if (!token) return;
    setBusy(true);
    const r = await deleteRule(rule.name, token);
    setBusy(false);
    if (r.ok) {
      void queryClient.invalidateQueries({ queryKey: ["rules"] });
      void queryClient.invalidateQueries({ queryKey: ["rules-derivations"] });
      onDeleted();
    } else {
      setError(r.message);
    }
  }

  return (
    <Modal
      title="Delete rule"
      onClose={onClose}
      busy={busy}
      danger
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button variant="dangerFilled" onClick={() => void handleDelete()} disabled={busy}>
            {busy ? "Deleting…" : "Delete"}
          </Button>
        </>
      }
    >
      <p className="text-sm text-tres-text-primary">
        Delete rule <span className="font-medium">{rule.name}</span>? Facts it
        derived go on the next rule run, and a query or a fact's context that
        refers to it as{" "}
        <code className="font-mono">{ruleScopeToken(rule.name)}</code> will
        pick out nothing.
      </p>
      {error && <p className={cn(messageClass('warning', 'normal'), 'mt-2')}>{error}</p>}
    </Modal>
  );
}
